import { AnimatePresence, m } from 'framer-motion';
import { useCallback } from 'react';
import type { ICookieViewProps } from './CookieView.interface';
import { classNameForElement, StyleElem, styleForElement } from './CookieView.style';

const STYLE_HIDDEN: { opacity: number } = { opacity: 0 };
const STYLE_VISIBLE: { opacity: number } = { opacity: 1 };

function OverlayView(props: ICookieViewProps): JSX.Element {
    return (
        <m.div
            aria-hidden
            className={classNameForElement(StyleElem.BackgroundOverlay, props)}
            style={styleForElement(StyleElem.BackgroundOverlay, props)}
            initial={STYLE_HIDDEN}
            animate={STYLE_VISIBLE}
            exit={STYLE_HIDDEN}
        />
    );
}

function CookieView(props: ICookieViewProps): JSX.Element {
    const style = useCallback((elem: StyleElem) => styleForElement(elem, props), [props]);
    const className = useCallback((elem: StyleElem) => classNameForElement(elem, props), [props]);

    return (
        <m.section
            role="dialog"
            aria-label="Cookie Popup"
            key="CookieView"
            className={className(StyleElem.Container)}
            style={style(StyleElem.Container)}
            initial={STYLE_HIDDEN}
            animate={STYLE_VISIBLE}
            exit={STYLE_HIDDEN}
        >
            <header
                className={className(StyleElem.TitleContainer)}
                style={style(StyleElem.TitleContainer)}
            >
                {props.translations.headingText}
            </header>
            <p
                className={className(StyleElem.BodyContainer)}
                style={style(StyleElem.BodyContainer)}
            >
                {props.translations.bodyText}
            </p>
            <div
                className={className(StyleElem.ButtonContainer)}
                style={style(StyleElem.ButtonContainer)}
            >
                <button
                    key="ButtonDecline"
                    aria-label="Decline"
                    className={className(StyleElem.ButtonDecline)}
                    style={style(StyleElem.ButtonDecline)}
                    onClick={props.onDecline}
                >
                    {props.translations.declineText}
                </button>
                <button
                    key="ButtonAccept"
                    aria-label="Accept"
                    className={className(StyleElem.ButtonAccept)}
                    style={style(StyleElem.ButtonAccept)}
                    onClick={props.onAccept}
                >
                    {props.translations.acceptText}
                </button>
                <a
                    key="LinkPrivacy"
                    aria-label="Privacy Policy"
                    className={className(StyleElem.ButtonPrivacyPolicy)}
                    style={style(StyleElem.ButtonPrivacyPolicy)}
                    href={props.privacyPolicyHref}
                >
                    {props.translations.privacyPolicyText}
                </a>
            </div>
        </m.section>
    );
}

function MainView(props: ICookieViewProps) {
    return (
        <AnimatePresence>
            <OverlayView key="analytics-cookie-overlay" {...props} />
            <CookieView key="analytics-cookie-dialog" {...props} />
        </AnimatePresence>
    );
}

export default MainView;
